import cloneDeep from "lodash-es/cloneDeep";
import isEqual from "lodash-es/isEqual";
import remove from "lodash-es/remove";

import { STRUCTURES, MASS_TABLE } from "./compound.data";
import { Compounds } from "#/declarations";

/**
 *  A collection of utility functions for calculating the layout
 *  of a molecule.
 */
export class CompoundUtils {
  /**
   * Parses the atomId and moleculeId from an atom's id in a dynamic build.
   */
  public static parseAtomId(atomId: string) {
    let regEx = /([A-Za-z0-9]*)-(.*)/g;
    let match = regEx.exec(atomId);

    if (match === null) {
      throw new Error(`Invalid atomId format: ${atomId}`);
    }

    return {
      atomId: match[1],
      moleculeId: match[2],
    };
  }

  /**
   * Parses the atomId and moleculeId from an atom's id in a dynamic build.
   */
  public static parseBondId(atomId: string) {
    let regEx = /([A-Za-z0-9]*)-([A-Za-z0-9]*)-(.*)/g;
    let match = regEx.exec(atomId);

    if (match === null) {
      throw new Error(`Invalid atomId format: ${atomId}`);
    }

    return {
      atom1Id: match[1],
      atom2Id: match[2],
      moleculeId: match[3],
    };
  }

  public static hasOpenBond(molecule: Compounds.Structure) {
    for (var i = 0; i < molecule.atoms.length; i++) {
      if (molecule.atoms[i].name === "R") {
        return true;
      }
    }
    return false;
  }

  /**
   * Determines if the atom should be labeled in the render.
   * TODO: checking the first character of the id string is not a reliable
   *    find a better way to detect if its a C / H / placeholder.
   */
  public static hasLabel(atomId: string, options: Compounds.Options): boolean {
    return !!(
      (options &&
        options.label &&
        options.label.length > 0 &&
        options.label.indexOf(atomId) > -1) ||
      (atomId[0] !== "c" && atomId[0] !== "p") ||
      (options && options.showHydrogen) //&& atomId[0] === "h" //TODO : Chris S need to fix this. This breaks after upgrading typescript
    );
  }

  /**
   * Adds label flags to any atoms in the `options.label` array.
   * @param molecule
   * @param options
   * @returns {Compounds.Structure}
   */
  public static addLabelFlags(
    molecule: Compounds.Structure,
    options: Compounds.Options,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.atoms.length; i++) {
      molecule.atoms[i].label = this.hasLabel(molecule.atoms[i].id, options);
    }
    return molecule;
  }

  /**
   * Gets a clone of a provided structure
   */
  public static getStructureById(id: string): Compounds.Structure {
    if (STRUCTURES.hasOwnProperty(id)) {
      // return a copy, don't modify constants.
      return cloneDeep(STRUCTURES[id]);
    } else {
      throw Error("Structure " + id + " does not exist.");
    }
  }

  /**
   * Gets a molecule structure based on an identifier string or structure
   */
  public static getStructure(structure: any): Compounds.Structure {
    let molecule = null;
    if (typeof structure === "string") {
      molecule = CompoundUtils.getStructureById(structure);
    } else if (typeof structure === "object" && structure !== null) {
      molecule = cloneDeep(structure);
    } else {
      throw Error("invalid render structure");
    }
    return molecule;
  }

  /**
   * Returns all bonds in the molecule that have `value` set for the passed
   * `propertyName`
   */
  public static getBondsByProperty(
    propertyName: string,
    value: any,
    molecule: Compounds.Structure,
  ): Array<Compounds.Bond> {
    let bonds = [];
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      if (bond.hasOwnProperty(propertyName)) {
        if (bond[propertyName] === value) {
          bonds.push(bond);
        }
      }
    }
    return bonds;
  }

  /**
   * Retrieves the atom by ID
   */
  public static getAtomById(molecule: Compounds.Structure, atomId: string) {
    if (molecule.atoms) {
      for (let i = 0; i < molecule.atoms.length; i++) {
        if (molecule.atoms[i].id === atomId) {
          return molecule.atoms[i];
        }
      }
    }
    throw Error("Could not find atom in molecule: " + atomId);
  }

  /**
   * Gets which bond number the bond is for the source atom in the bond.
   */
  public static getLinkNumber(
    molecule: Compounds.Structure,
    bond: Compounds.Bond,
  ): number {
    let count = 0;
    for (let i = 0; i < molecule.bonds.length; i++) {
      if (molecule.bonds[i].source === bond.source && i !== 0) {
        count++;
        if (molecule.bonds[i].target === bond.target) {
          return count;
        }
      }
    }
    return -1; // the passed bond doesn't exist
  }

  /**
   * Goes through a molecule and returns how many atoms of type `atomName`
   * exist.
   * @param molecule
   * @param atomName
   * @returns {number}
   */
  public static getAtomCount(molecule: Compounds.Structure, atomName: string) {
    let count = 0;
    for (let i = 0; i < molecule.atoms.length; i++) {
      if (molecule.atoms[i].name === atomName) {
        count++;
      }
    }
    return count;
  }

  /**
   * Checks to see if the provided atomId in the molecule is hydrogen.
   */
  public static isHydrogen(
    molecule: Compounds.Structure,
    atomId: string,
  ): boolean {
    let atom = this.getAtomById(molecule, atomId);
    return atom.name === "H";
  }

  /**
   * Generated a UUID, taken from:
   * http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
   * @returns {string}
   */
  public static generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  /**
   * Removes the atom with the specified atomId from the molecule.
   * @param atomId
   * @param molecule
   * @returns {Compounds.Atom} - the atom that was removed
   */
  public static removeAtom(molecule: Compounds.Structure, atomId: string) {
    for (let i = 0; i < molecule.atoms.length; i++) {
      if (molecule.atoms[i].id === atomId) {
        return molecule.atoms.splice(i, 1)[0];
      }
    }
    throw Error("No atom with the id '" + atomId + "' found.");
  }

  /**
   * Removes a bond with the specified atomId as the target
   * @param atomId
   * @param molecule
   * @returns {Compounds.Bond} - the bond that was removed
   */
  public static removeBondWithTarget(
    molecule: Compounds.Structure,
    atomId: string,
  ) {
    for (let i = 0; i < molecule.bonds.length; i++) {
      if (molecule.bonds[i].target === atomId) {
        let removed = molecule.bonds.splice(i, 1);
        return removed[0];
      }
    }
    throw Error("No bond with the target '" + atomId + "' found.");
  }

  /**
   * Replaces the target property on the bond that has oldTargetId as the
   * target property.
   * @param oldTargetId
   * @param newTargetId
   * @param molecule
   * @returns {number} - index of the bond that was modified
   */
  public static replaceBondTarget(
    molecule: Compounds.Structure,
    oldTargetId: string,
    newTargetId: string,
  ) {
    for (let i = 0; i < molecule.bonds.length; i++) {
      if (molecule.bonds[i].target === oldTargetId) {
        molecule.bonds[i].target = newTargetId;
        return i;
      }
    }
    return -1;
  }

  /**
   * Appends the provided unique identifier to each of the atoms and bonds
   * in the molecule. Mutates the molecule parameter.
   * @param uuid
   * @param molecule
   * @returns {Compounds.Structure}
   */
  public static addIdsToMolecule(uuid: string, molecule: Compounds.Structure) {
    for (let i = 0; i < molecule.atoms.length; i++) {
      molecule.atoms[i].id += "-" + uuid;
    }
    for (let i = 0; i < molecule.bonds.length; i++) {
      molecule.bonds[i].id += "-" + uuid;
      molecule.bonds[i].source += "-" + uuid;
      molecule.bonds[i].target += "-" + uuid;
    }
    return molecule;
  }

  /**
   * Swaps a bond's source and target when the target is the provided `source`
   * id, and the source and target belong to the same molecule.
   * @param moleculeId
   * @param source
   * @param molecule
   * @returns {Compounds.Structure}
   */
  public static swapSourceTarget(
    moleculeId: string,
    source: string,
    molecule: Compounds.Structure,
  ) {
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      if (
        bond.target === source &&
        CompoundUtils.isPartOfMolecule(bond.source, moleculeId)
      ) {
        let source = bond.target;
        bond.target = bond.source;
        bond.source = source;
      }
    }
    return molecule;
  }

  /**
   * Check if the atomId passed is part of an aromatic ring structure by
   * following the links from the `atomId` until it passes a threshold
   * or returns back to itself.
   */
  public static isAtomInAromaticRing(
    atomId: string,
    molecule: Compounds.Structure,
    search: any = false,
  ) {
    if (search) {
      // success condition
      if (search.rootAtomId === atomId) {
        return search.double === 3 && search.single === 3;
      }
      // fail conditions
      if (search.single + search.double > 5) {
        return false;
      }
      if (search.checked.indexOf(atomId) > -1) {
        return false;
      }
    } else {
      search = {
        rootAtomId: atomId,
        checked: [],
        single: 0,
        double: 0,
        count: 0,
      };
    }
    let isAromatic = false;
    search.checked.push(atomId);
    // get all bonds involving this atom
    let bonds = this.getBondsByProperty("source", atomId, molecule).concat(
      this.getBondsByProperty("target", atomId, molecule),
    );
    bonds.forEach((d) => {
      let otherAtom;
      if (d.source === atomId) {
        otherAtom = this.getAtomById(molecule, d.target);
      } else {
        otherAtom = this.getAtomById(molecule, d.source);
      }
      if (otherAtom.name === "C") {
        if (!search.bondType || search.bondType === 1) {
          if (d.type === 1) {
            search.single++;
            search.bondType = 2;
            isAromatic = this.isAtomInAromaticRing(
              otherAtom.id,
              molecule,
              search,
            );
          }
        }
        if ((!search.bondType || search.bondType === 2) && !isAromatic) {
          if (d.type === 2) {
            search.double++;
            search.bondType = 1;
            isAromatic = this.isAtomInAromaticRing(
              otherAtom.id,
              molecule,
              search,
            );
          }
        }
      }
    });
    return isAromatic;
  }

  /**
   * Insert any bonds with the source immediately after the provided index.
   * Mutates the bonds array in flatMolecule.bonds
   */
  public static orderBonds(
    sourceAtom: string,
    index: number,
    flatMolecule: Compounds.Structure,
  ) {
    if (this.isAtomInAromaticRing(sourceAtom, flatMolecule)) {
      return flatMolecule;
    } else {
      let bonds = flatMolecule.bonds;
      let removed = remove(bonds, (d) => d.source === sourceAtom);
      bonds.splice(index + 1, 0, ...removed);
      return flatMolecule;
    }
  }

  /**
   * Checks if the provided atomId belongs to the molecule with the provided
   * ID.
   * TODO: Vulnerable to errors, as atomId could still contain moleculeId, but
   * not actually be part of the molecule.  Instead, use a regex to get the
   * value after the first hyphen and check if it is the moleculeId.
   * @param atomId
   * @param moleculeId
   * @returns {boolean}
   */
  public static isPartOfMolecule(atomId: string | any[], moleculeId: string) {
    return atomId.includes(moleculeId);
  }

  /**
   * Compares two molecules to check if they are the same isomer of one another
   */
  public static areIdentical(
    molecule1: Compounds.Structure,
    molecule2: Compounds.Structure,
  ): boolean {
    //Compare atom and bond counts
    if (
      molecule1.atoms.length !== molecule2.atoms.length ||
      molecule1.bonds.length !== molecule2.bonds.length
    ) {
      return false;
    }
    //TODO: Consider refactoring to ungroup these checks. Evaluation no longer
    //      short circuits on atom comparison, making for a longer check in all
    //      non-atom-perfect comparisons
    //NOTE: Atom bin comparison may not be necessary - molecule exist which
    //      would be valid even with different structures, but it's unclear if
    //      they're buildable in the app. Suggest leaving for now.
    return isEqual(this.binMolecule(molecule1), this.binMolecule(molecule2));
  }

  /**
   * Compares two molecules to check if they have the same atoms
   */
  public static haveSameAtoms(
    molecule1: Compounds.Structure,
    molecule2: Compounds.Structure,
  ): boolean {
    return isEqual(this.binAtoms(molecule1), this.binAtoms(molecule2));
  }

  /**
   * Compares two molecules to check if they have the same bonds
   */
  public static haveSameBonds(
    molecule1: Compounds.Structure,
    molecule2: Compounds.Structure,
  ): boolean {
    return isEqual(this.binBonds(molecule1), this.binBonds(molecule2));
  }

  /**
   * Returns the mass of a structure based upon its atoms
   * TODO: Mass constants should not be in the compound utilites, move elsewhere
   */
  public static compoundMass(molecule: Compounds.Structure): number {
    let newMass = 0;
    for (let atom of molecule.atoms) {
      if (atom.name && MASS_TABLE.hasOwnProperty(atom.name)) {
        newMass += MASS_TABLE[atom.name];
      }
    }
    return newMass;
  }

  /**
   * Checks if a given atom ID is marked to be labelled.
   */
  public static hasLabelFlag(atomId: string, molecule: Compounds.Structure) {
    for (let i = 0; i < molecule.atoms.length; i++) {
      if (molecule.atoms[i].id === atomId) {
        return molecule.atoms[i].label === true;
      }
    }
    return false;
  }

  /**
   * Gets the number of connections an atom has.
   */
  public static getConnectionCount(
    molecule: Compounds.Structure,
    atom: Compounds.Atom,
  ): number {
    let count = 0;
    for (let i = 0; i < molecule.bonds.length; i++) {
      let mol = molecule.bonds[i];
      if (mol.source === atom.id || mol.target === atom.id) {
        count++;
      }
    }
    return count;
  }

  /**
   * Increments the value of the property of an object.
   */
  private static incrementProperty(bin: any, property: string) {
    bin.hasOwnProperty(property) ? bin[property]++ : (bin[property] = 1);
    return bin;
  }

  /**
   * Creates an object with a count of atoms / bonds types
   */
  private static binMolecule(molecule: Compounds.Structure) {
    return {
      atoms: this.binAtoms(molecule),
      bonds: this.binBonds(molecule),
      neighbours: this.binNeighbours(molecule),
    };
  }

  /**
   * Utility function for isomer checking. Counts atoms by type.
   */
  private static binAtoms(molecule: Compounds.Structure): any {
    let bin = {};
    for (let atom of molecule.atoms) {
      if (atom.name) {
        bin = this.incrementProperty(bin, atom.name);
      }
    }
    return bin;
  }

  /**
   * Utility function for isomer checking. Counts bonds by type.
   */
  private static binBonds(molecule: Compounds.Structure): any {
    let bin = {};
    for (let bond of molecule.bonds) {
      let source = CompoundUtils.getAtomById(molecule, bond.source);
      let target = CompoundUtils.getAtomById(molecule, bond.target);
      if (source && target) {
        bin = this.incrementProperty(bin, source.name + "" + target.name);
        bin = this.incrementProperty(bin, target.name + "" + source.name);
      }
    }
    return bin;
  }

  /**
   * Utility function for isomer checking. Produces a sorted neighbour list for
   * each hydrogen
   */
  private static binNeighbours(molecule: Compounds.Structure): any {
    let bin = [];
    for (let atom of molecule.atoms) {
      if (atom.name === "H") {
        bin.push(
          this.getAtomNeighbours(molecule, atom).map((d) => {
            return d.name || "";
          }),
        );
      }
    }
    return bin
      .map((d) => {
        return d.sort().join();
      })
      .sort();
  }

  /**
   * Gets all neighbours of a given atom in the molecule.
   */
  private static getAtomNeighbours(
    molecule: Compounds.Structure,
    atom: Compounds.Atom,
  ): Array<Compounds.Atom> {
    let parents = this.getAtomLinks(molecule, atom);
    let grandParents = [];
    //These are the hosts of the neighbours
    for (let parent of parents) {
      grandParents.push(...this.getAtomLinks(molecule, parent));
    }
    let neighbours = [];
    //These are the neighbours themselves
    for (let parent of grandParents) {
      neighbours.push(...this.getAtomLinks(molecule, parent));
    }
    return neighbours;
  }

  /**
   * Get all atoms that link to a given atom in a molecule.
   */
  private static getAtomLinks(
    molecule: Compounds.Structure,
    atom: Compounds.Atom,
  ): Array<Compounds.Atom> {
    let links = [];
    //Get all parents of this atom
    for (let bond of molecule.bonds) {
      if (bond.source === atom.id) {
        links.push(CompoundUtils.getAtomById(molecule, bond.target));
      } else if (bond.target === atom.id) {
        links.push(CompoundUtils.getAtomById(molecule, bond.source));
      }
    }
    return links;
  }
}
